export type LayoutStructure = 'flow' | 'checklist' | 'media-rows' | 'text-media';

export type LayoutDefinition = {
  base?: string;
  structure?: LayoutStructure;
  fitTitle?: boolean;
  variables?: Record<string, string>;
};

export type AnimationInstance = {
  mount(target: HTMLElement): void;
  unmount(): void;
};

export type AnimationFactory = () => AnimationInstance;

export type AnimationDefinition = {
  id?: string;
  prefix?: string;
  factory: AnimationFactory;
};

export type BackgroundController = {
  update(target: HTMLElement, background: string): void | Promise<void>;
  destroy(): void;
};

export type ThemeDefinition = {
  id: string;
  extends?: string;
  defaultBackground?: string;
  tokens?: Record<string, string>;
  layouts?: Record<string, LayoutDefinition>;
  animations?: AnimationDefinition[];
  createBackground?: () => BackgroundController;
};

export type ResolvedLayout = LayoutDefinition & {
  id: string;
  requested: string;
};

const DEFAULT_THEME = 'default';
const DEFAULT_LAYOUT = 'content';

export class PresentationRegistry {
  #themes = new Map<string, ThemeDefinition>();

  registerTheme(theme: ThemeDefinition): void {
    if (!theme.id.trim()) throw new Error('A theme must have an id.');
    this.#themes.set(theme.id, theme);
  }

  hasTheme(id: string): boolean {
    return this.#themes.has(id);
  }

  resolveThemeId(requested = DEFAULT_THEME): string {
    return this.#themes.has(requested) ? requested : DEFAULT_THEME;
  }

  resolveLayout(themeId: string, requested = DEFAULT_LAYOUT): ResolvedLayout {
    const theme = this.resolveThemeId(themeId);
    const match = this.findInChain(theme, (candidate) => candidate.layouts?.[requested]);
    if (match) return {id: requested, requested, structure: 'flow', ...match};
    const fallback = this.findInChain(theme, (candidate) => candidate.layouts?.[DEFAULT_LAYOUT]);
    return {id: DEFAULT_LAYOUT, requested, structure: 'flow', ...fallback};
  }

  resolveTokens(themeId: string): Record<string, string> {
    const chain = this.themeChain(this.resolveThemeId(themeId)).reverse();
    return Object.assign({}, ...chain.map((theme) => theme.tokens ?? {}));
  }

  defaultBackground(themeId: string): string {
    return (
      this.findInChain(
        this.resolveThemeId(themeId),
        (theme) => theme.defaultBackground,
      ) ?? 'plain'
    );
  }

  animationFactory(themeId: string, component: string): AnimationFactory | undefined {
    return this.findInChain(this.resolveThemeId(themeId), (theme) =>
      theme.animations?.find(
        (definition) =>
          definition.id === component ||
          (definition.prefix !== undefined && component.startsWith(definition.prefix)),
      )?.factory,
    );
  }

  createBackground(themeId: string): BackgroundController | undefined {
    return this.findInChain(
      this.resolveThemeId(themeId),
      (theme) => theme.createBackground,
    )?.();
  }

  private findInChain<T>(
    themeId: string,
    select: (theme: ThemeDefinition) => T | undefined,
  ): T | undefined {
    for (const theme of this.themeChain(themeId)) {
      const result = select(theme);
      if (result !== undefined) return result;
    }
    return undefined;
  }

  private themeChain(themeId: string): ThemeDefinition[] {
    const chain: ThemeDefinition[] = [];
    const visited = new Set<string>();
    let current = this.#themes.get(themeId) ?? this.#themes.get(DEFAULT_THEME);
    while (current && !visited.has(current.id)) {
      chain.push(current);
      visited.add(current.id);
      current = current.extends ? this.#themes.get(current.extends) : undefined;
    }
    return chain;
  }
}

export const presentationRegistry = new PresentationRegistry();
